import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Layout from "@/components/Layout";
import { useActiveTransferDestinations } from "@/hooks/useTransferDestinations";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, DollarSign, ArrowRight } from "lucide-react";

const TransferDestinations = () => {
  const { data: destinations, isLoading } = useActiveTransferDestinations();

  return (
    <Layout>
      <Helmet>
        <title>Transfer Destinations | Premium Transportation Service</title>
        <meta
          name="description"
          content="Discover our transfer destinations in the Dominican Republic. Premium service, competitive prices, and 24/7 availability."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/10 via-background to-primary/5 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Transfer Destinations</h1>
            <p className="text-xl text-muted-foreground">
              Premium transportation service to the best destinations in the Dominican Republic
            </p>
          </div>
        </div>
      </section>

      {/* Destinations Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading destinations...</p>
            </div>
          ) : destinations && destinations.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {destinations.map((destination) => (
                <Card
                  key={destination.id}
                  className="overflow-hidden hover:shadow-xl transition-all duration-300 group"
                >
                  {destination.hero_image && (
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={destination.hero_image}
                        alt={destination.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <CardContent className="p-6">
                    <h2 className="text-2xl font-bold mb-2">{destination.name}</h2>
                    {destination.description && (
                      <p className="text-muted-foreground mb-4 line-clamp-2">
                        {destination.description}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-2 mb-4">
                      {destination.estimated_time && (
                        <Badge variant="secondary">
                          <Clock className="h-4 w-4 mr-1" />
                          {destination.estimated_time}
                        </Badge>
                      )}
                      {destination.from_airport_price && (
                        <Badge variant="secondary">
                          <DollarSign className="h-4 w-4 mr-1" />
                          From ${destination.from_airport_price}
                        </Badge>
                      )}
                    </div>

                    {destination.highlights && destination.highlights.length > 0 && (
                      <ul className="space-y-1 mb-4">
                        {destination.highlights.slice(0, 3).map((highlight, index) => (
                          <li key={index} className="text-sm text-muted-foreground flex items-start">
                            <span className="text-primary mr-2">✓</span>
                            {highlight}
                          </li>
                        ))}
                      </ul>
                    )}

                    <Link to={`/traslados/${destination.slug}`}>
                      <Button className="w-full group">
                        View More
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                No destinations available at this time.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Can't find your destination?</h2>
            <p className="text-muted-foreground mb-8">
              Contact us and we'll help you arrange your transfer to any destination in
              the Dominican Republic
            </p>
            <Link to="/contact">
              <Button size="lg">Contact Us Now</Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TransferDestinations;